/**
 * Live camera search — consumes sections/live-search.liquid.
 *
 * Responsibilities
 *  1. Continuous camera frames to POST /api/search (unified — vision mode).
 *  2. Upload fallback with OCR-first path (Tesseract → text search)
 *     and Gemini vision fallback, matching prive-brain.js hybrid.
 *  3. Render matched product with PDP parity (familia/concentración/
 *     género/accords/notes/rating/clima/ocasión/duración).
 *  4. For logged-in customers with qualifying past orders, POST
 *     /api/similarity and render purchase-similarity chips.
 *  5. Wire PriveBrainIntel.linkCustomer so intel events carry CID.
 *
 * All dynamic content is built via document.createElement + textContent.
 * No innerHTML usage — keeps parity with prive-brain.js conventions and
 * avoids XSS risk on data coming back from the search API.
 *
 * Inputs (injected by sections/live-search.liquid):
 *   window.PRIVE_LIVE_SEARCH_CONFIG = { apiBase, scanMs, preset, waPhone, customerId, storefrontRoot }
 *   window.PRIVE_USER_PURCHASES    = [handle, ...]
 */
(function() {
  'use strict';

  var cfg = window.PRIVE_LIVE_SEARCH_CONFIG || {};
  var API = cfg.apiBase || 'https://prive-search.fly.dev';
  var INTERVAL = cfg.scanMs || 1200;
  var PURCHASES = Array.isArray(window.PRIVE_USER_PURCHASES)
    ? window.PRIVE_USER_PURCHASES.filter(Boolean)
    : [];
  var CUSTOMER_ID = cfg.customerId || '';
  var W = 640, H = 480, QUAL = 0.7;

  var video = document.getElementById('video');
  var canvas = document.getElementById('canvas');
  var ctx = canvas.getContext('2d');
  var particlesCanvas = document.getElementById('particles');
  var pCtx = particlesCanvas.getContext('2d');
  canvas.width = W; canvas.height = H;

  var scanLine = document.getElementById('scanLine');
  var corners = ['c1', 'c2', 'c3', 'c4'].map(function(id) { return document.getElementById(id); });
  var statusPill = document.getElementById('statusPill');
  var statusText = document.getElementById('statusText');
  var idOverlay = document.getElementById('idOverlay');
  var idName = document.getElementById('idName');
  var idDetail = document.getElementById('idDetail');
  var confFill = document.getElementById('confFill');
  var productPanel = document.getElementById('productPanel');
  var errorToast = document.getElementById('errorToast');
  var errTitle = document.getElementById('errTitle');
  var errMsg = document.getElementById('errMsg');
  var permScreen = document.getElementById('permScreen');
  var errorScreen = document.getElementById('errorScreen');
  var tapOverlay = document.getElementById('tapOverlay');
  var scanHint = document.getElementById('scanHint');

  var inflight = false;
  var scanTimer = null;
  var stream = null;
  var lastHandle = null;
  var particles = [];
  var animFrame = null;
  var uploadMode = false;
  var scanningPaused = false;

  // Camera flash overlay (iPhone-style)
  var flashOverlay = document.createElement('div');
  flashOverlay.style.cssText = 'position:absolute;inset:0;z-index:50;background:#fff;opacity:0;pointer-events:none;transition:opacity 0.08s ease';
  document.getElementById('viewport').appendChild(flashOverlay);

  function cameraFlash() {
    flashOverlay.style.opacity = '0.85';
    setTimeout(function() { flashOverlay.style.opacity = '0'; }, 100);
  }
  var shutterSound = new Audio(window.PRIVE_SHUTTER_URL || 'https://quicksounds.com/uploads/tracks/549749701_821945641_128383037.mp3');
  shutterSound.volume = 0.7;
  var _ocrPromise = null;
  var activeRenderToken = 0;
  var cameraStarting = false;
  var purchasesLoadPromise = null;
  var firstScan = true;

  if (CUSTOMER_ID && window.PriveBrainIntel && typeof window.PriveBrainIntel.linkCustomer === 'function') {
    try { window.PriveBrainIntel.linkCustomer(String(CUSTOMER_ID)); } catch (e) { /* noop */ }
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function svgEl(tag, attrs) {
    var e = document.createElementNS('http://www.w3.org/2000/svg', tag);
    if (attrs) for (var k in attrs) if (attrs.hasOwnProperty(k)) e.setAttribute(k, attrs[k]);
    return e;
  }
  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
    return node;
  }
  function getActiveProductCard(expectedHandle) {
    var card = productPanel.querySelector('.product-card');
    if (!card) return null;
    if (expectedHandle && card.getAttribute('data-handle') !== expectedHandle) return null;
    return card;
  }
  function normalizeHandle(value) {
    return typeof value === 'string' ? value.trim() : '';
  }
  function normalizeStorefrontRoot(root) {
    if (typeof root !== 'string') return '/';
    var trimmed = root.trim();
    if (!trimmed || trimmed === '/') return '/';
    var parts = trimmed.split('/').filter(Boolean);
    return parts.length ? '/' + parts.join('/') + '/' : '/';
  }
  function activeStorefrontRoot() {
    var shopifyRoot = window.Shopify &&
      window.Shopify.routes &&
      typeof window.Shopify.routes.root === 'string'
      ? window.Shopify.routes.root
      : '';
    return normalizeStorefrontRoot(shopifyRoot || cfg.storefrontRoot || '/');
  }
  function productPath(handle) {
    return activeStorefrontRoot() + 'products/' + normalizeHandle(handle);
  }
  function uniqueHandles(values) {
    var seen = Object.create(null);
    return (values || []).map(normalizeHandle).filter(function(handle) {
      if (!handle || seen[handle]) return false;
      seen[handle] = true;
      return true;
    });
  }
  function createRetryableMemoizedLoader(load) {
    return function run() {
      if (purchasesLoadPromise) return purchasesLoadPromise;
      purchasesLoadPromise = Promise.resolve()
        .then(load)
        .catch(function(error) {
          purchasesLoadPromise = null;
          throw error;
        });
      return purchasesLoadPromise;
    };
  }

  function initParticles() {
    var rect = particlesCanvas.getBoundingClientRect();
    particlesCanvas.width = rect.width;
    particlesCanvas.height = rect.height;
    particles = [];
    for (var i = 0; i < 18; i++) {
      particles.push({
        x: Math.random() * rect.width, y: Math.random() * rect.height,
        vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5, a: Math.random() * 0.3 + 0.2
      });
    }
    drawParticles();
  }
  function drawParticles() {
    var cw = particlesCanvas.width, ch = particlesCanvas.height;
    pCtx.clearRect(0, 0, cw, ch);
    particles.forEach(function(p) {
      p.x += p.vx; p.y += p.vy;
      if (p.x < 0) p.x = cw; if (p.x > cw) p.x = 0;
      if (p.y < 0) p.y = ch; if (p.y > ch) p.y = 0;
      pCtx.beginPath();
      pCtx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      pCtx.fillStyle = 'rgba(184,149,106,' + p.a + ')';
      pCtx.fill();
    });
    animFrame = requestAnimationFrame(drawParticles);
  }

  function fmtPrice(v) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    return isNaN(n) ? '' : 'L ' + n.toLocaleString('es-HN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  function productImg(url) {
    if (!url) return '';
    if (url.indexOf('cdn.shopify.com') < 0) return url;
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return url + sep + 'width=160&format=jpg';
  }
  function fmtCountK(n) {
    if (!n && n !== 0) return '';
    if (n >= 1000) {
      var k = n / 1000;
      return (k >= 10 ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, '')) + 'K';
    }
    return String(n);
  }
  function setStatus(text, mode) {
    statusText.textContent = text;
    statusPill.className = 'status-pill' + (mode ? ' ' + mode : '');
  }
  function setScan(on) {
    var method = on ? 'add' : 'remove';
    scanLine.classList[method]('active');
    corners.forEach(function(c) { c.classList[method]('active'); });
  }
  function setBrackets(cls) {
    corners.forEach(function(c) { c.classList.remove('pulse', 'no-match'); });
    if (cls) corners.forEach(function(c) { c.classList.add(cls); });
  }
  function showToast(title, msg) {
    errTitle.textContent = title;
    errMsg.textContent = msg;
    errorToast.classList.add('visible');
    setTimeout(function() { errorToast.classList.remove('visible'); }, 4000);
  }
  function showError(title, msg) {
    document.getElementById('errorTitle').textContent = title;
    document.getElementById('errorMsg').textContent = msg;
    errorScreen.classList.remove('hidden');
    permScreen.classList.add('hidden');
  }
  function parseList(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  function translateGenero(g) {
    if (!g) return '';
    var key = String(g).toUpperCase();
    return key === 'M' ? 'Masculino' : key === 'F' ? 'Femenino' : key === 'U' ? 'Unisex' : g;
  }

  function postVision(blob, onResult, onEmpty, onError) {
    var fd = new FormData();
    fd.append('image', blob, blob.name || 'image.jpg');
    var ctrl = new AbortController();
    var to = setTimeout(function() { ctrl.abort(); }, 12000);

    fetch(API + '/api/search', { method: 'POST', body: fd, signal: ctrl.signal })
      .then(function(r) {
        clearTimeout(to);
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function(data) {
        if (data && data.results && data.results.length) onResult(data);
        else if (onEmpty) onEmpty();
      })
      .catch(function(err) { if (onError) onError(err); });
  }

  function getOcrWorker() {
    if (_ocrPromise) return _ocrPromise;
    _ocrPromise = new Promise(function(resolve, reject) {
      if (window.Tesseract) return resolve(window.Tesseract);
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
      s.async = true;
      s.onload = function() { resolve(window.Tesseract); };
      s.onerror = function() {
        _ocrPromise = null;
        reject(new Error('Tesseract load failed'));
      };
      document.head.appendChild(s);
    }).then(function(Tesseract) {
      return Tesseract.createWorker('eng', 1, { logger: function() {} });
    });
    return _ocrPromise;
  }

  function showIdentification(data) {
    var id = data.identification;
    if (!id || !id.brand) return;
    idName.textContent = id.brand + ' ' + (id.name || '');
    var parts = [];
    if (id.concentration) parts.push(id.concentration);
    if (id.bottle_size) parts.push(id.bottle_size);
    idDetail.textContent = parts.join(' · ');
    var conf = id.confidence != null ? Math.round(id.confidence * 100) : 0;
    confFill.style.width = conf + '%';
    idOverlay.classList.add('visible');
  }

  function starNode(filled) {
    var s = svgEl('svg', { viewBox: '0 0 24 24', class: filled ? 'star-full' : 'star-empty', 'aria-hidden': 'true' });
    var path = svgEl('path', { d: 'M12 2l2.9 6.6 7.1.6-5.4 4.8 1.6 7L12 17.3 5.8 21l1.6-7L2 9.2l7.1-.6z' });
    s.appendChild(path);
    return s;
  }
  function starsRow(rating) {
    var row = el('div', 'star-rating');
    var r = Math.max(0, Math.min(5, Number(rating) || 0));
    for (var i = 1; i <= 5; i++) row.appendChild(starNode(r >= i - 0.5));
    return row;
  }

  function climaPct(votes) {
    if (!votes) return null;
    var fresh = (Number(votes.spring) || 0) + (Number(votes.summer) || 0);
    var warm = (Number(votes.fall) || 0) + (Number(votes.autumn) || 0) + (Number(votes.winter) || 0);
    var total = warm + fresh;
    if (total <= 0) return null;
    return { warm: Math.round(warm / total * 100), fresh: Math.round(fresh / total * 100) };
  }
  function ocasionPct(votes) {
    if (!votes) return null;
    var day = Number(votes.day) || 0;
    var night = Number(votes.night) || 0;
    var total = day + night;
    if (total <= 0) return null;
    return { dia: Math.round(day / total * 100), noche: Math.round(night / total * 100) };
  }
  function duracionScore(votes) {
    if (!votes) return 0;
    var love = Number(votes.love_it) || 0;
    var like = Number(votes.like_it) || 0;
    var neu = Number(votes.neutral) || 0;
    var dis = Number(votes.dislike_it) || 0;
    var hate = Number(votes.hate_it) || 0;
    var total = love + like + neu + dis + hate;
    if (total <= 0) return 0;
    return (5 * love + 4 * like + 3 * neu + 2 * dis + 1 * hate) / total;
  }
  function duracionLabel(score) {
    if (score >= 4.5) return 'Excelente';
    if (score >= 3.5) return 'Larga';
    if (score >= 2.5) return 'Media';
    if (score >= 1.5) return 'Poca';
    if (score > 0) return 'Muy poca';
    return '';
  }

  function metaLabel(text) { return el('div', 'meta-label', text); }
  function metaSection(label, children) {
    var section = el('div', 'meta-section');
    section.appendChild(metaLabel(label));
    children.forEach(function(child) { if (child) section.appendChild(child); });
    return section;
  }
  function pctSplitNode(left, right) {
    var wrap = el('div', 'pct-split');
    [left, right].forEach(function(item, index) {
      var it = el('div', 'pct-item');
      it.appendChild(el('span', 'pct-value', item.value + '%'));
      it.appendChild(el('span', 'pct-name', item.name));
      wrap.appendChild(it);
      if (index === 0) wrap.appendChild(el('span', 'pct-divider', '·'));
    });
    return wrap;
  }
  function duracionBarNode(score) {
    var bar = el('div', 'duration-bar');
    var active = Math.max(0, Math.min(5, Math.round(score)));
    for (var i = 1; i <= 5; i++) {
      bar.appendChild(el('div', 'duration-seg' + (i <= active ? ' active' : '')));
    }
    return bar;
  }

  function pyramidSection(label, notes, noteClass, startDelay) {
    if (!notes.length) return null;
    var section = el('div', 'pyramid-section');
    section.appendChild(el('div', 'pyramid-label', label));
    var pills = el('div', 'pyramid-pills');
    var delay = startDelay;
    notes.forEach(function(note) {
      var pill = el('span', 'note-pill ' + noteClass, note);
      pill.style.animationDelay = delay + 'ms';
      pills.appendChild(pill);
      delay += 50;
    });
    section.appendChild(pills);
    return section;
  }

  function dismissButton() {
    var b = document.createElement('button');
    b.className = 'btn-dismiss';
    b.id = 'btnDismiss';
    b.setAttribute('aria-label', 'Cerrar');
    return b;
  }

  function cardTopNode(result) {
    var top = el('div', 'card-top');
    var imgWrap = el('div', 'card-img');
    if (result.image) {
      var img = document.createElement('img');
      img.src = productImg(result.image);
      img.alt = '';
      img.loading = 'eager';
      imgWrap.appendChild(img);
    }
    top.appendChild(imgWrap);

    var info = el('div', 'card-info');
    info.appendChild(el('div', 'card-vendor', result.vendor || ''));
    info.appendChild(el('div', 'card-title', result.title || ''));

    var priceRow = el('div', 'card-price-row');
    priceRow.appendChild(el('span', 'card-price', fmtPrice(result.price)));
    if (result.compareAtPrice && result.compareAtPrice > result.price) {
      priceRow.appendChild(el('span', 'card-compare', fmtPrice(result.compareAtPrice)));
    }
    var stockClass = (result.inventory != null && result.inventory <= 0) ? 'out' : 'in';
    priceRow.appendChild(el('span', 'card-stock ' + stockClass, stockClass === 'in' ? 'Disponible' : 'Agotado'));
    info.appendChild(priceRow);

    top.appendChild(info);
    return top;
  }

  function fragDetailsNode(profileData) {
    if (!profileData) return null;
    var p = profileData.profile || profileData;

    var details = el('div', 'frag-details');
    details.appendChild(el('div', 'frag-divider'));

    var badges = [];
    if (p.familia) badges.push({ t: p.familia, accent: false });
    if (p.concentracion) badges.push({ t: p.concentracion, accent: true });
    var generoLabel = translateGenero(p.genero);
    if (generoLabel) badges.push({ t: generoLabel, accent: false });
    if (badges.length) {
      var row = el('div', 'badge-row');
      badges.forEach(function(badge) {
        row.appendChild(el('span', 'frag-badge' + (badge.accent ? ' accent' : ''), badge.t));
      });
      details.appendChild(row);
    }

    var topN = Array.isArray(p.topNotes) ? p.topNotes : parseList(p.notas_salida);
    var midN = Array.isArray(p.heartNotes) ? p.heartNotes : parseList(p.notas_corazon);
    var baseN = Array.isArray(p.baseNotes) ? p.baseNotes : parseList(p.notas_fondo);
    var delay = 0;
    var s1 = pyramidSection('Salida', topN, 'top', delay);
    if (s1) { details.appendChild(s1); delay += topN.length * 50; }
    var s2 = pyramidSection('Corazón', midN, 'mid', delay);
    if (s2) { details.appendChild(s2); delay += midN.length * 50; }
    var s3 = pyramidSection('Fondo', baseN, 'base', delay);
    if (s3) { details.appendChild(s3); delay += baseN.length * 50; }

    var accords = Array.isArray(p.mainAccords) ? p.mainAccords : parseList(p.main_accords);
    if (accords.length) {
      var accordSection = pyramidSection('Acordes', accords, 'accord', delay);
      if (accordSection) details.appendChild(accordSection);
    }

    var meta = [];
    var result = profileData._result || {};
    var resultRating = Number(result.rating || p.rating);
    var profileRating = Number(profileData.rating);
    var rRating = Number.isFinite(resultRating) && resultRating > 0
      ? resultRating
      : (Number.isFinite(profileRating) && profileRating > 0 ? profileRating : 0);
    var resultRatingCount = Number(result.rating_count || p.totalVotes);
    var profileRatingCount = Number(profileData.rating_count || p.totalVotes);
    var rCount = Number.isFinite(resultRatingCount)
      ? resultRatingCount
      : (Number.isFinite(profileRatingCount) ? profileRatingCount : 0);
    if (rRating > 0) {
      meta.push(metaSection('Calificación', [
        el('div', 'meta-value rating', rRating.toFixed(1)),
        starsRow(rRating),
        el('div', 'meta-subtext', fmtCountK(rCount) + ' opiniones')
      ]));
    }

    var clima = climaPct(profileData.seasonVotes || profileData.season_votes);
    if (clima) {
      meta.push(metaSection('Clima', [
        pctSplitNode({ value: clima.warm, name: 'Cálido' }, { value: clima.fresh, name: 'Fresco' })
      ]));
    }

    var oca = ocasionPct(profileData.timeOfDayVotes || profileData.time_of_day_votes);
    if (oca) {
      meta.push(metaSection('Ocasión', [
        pctSplitNode({ value: oca.dia, name: 'Día' }, { value: oca.noche, name: 'Noche' })
      ]));
    }

    var dScore = duracionScore(profileData.longevityVotes || profileData.longevity_votes);
    if (dScore > 0) {
      meta.push(metaSection('Duración', [
        el('div', 'meta-value', duracionLabel(dScore)),
        duracionBarNode(dScore)
      ]));
    }

    if (meta.length) {
      var grid = el('div', 'product-metadata');
      meta.forEach(function(item) { grid.appendChild(item); });
      details.appendChild(grid);
    }

    return details;
  }

  function renderProductCard(result, profileData) {
    var card = el('div', 'product-card');
    card.setAttribute('data-handle', result.handle || '');
    card.appendChild(dismissButton());
    card.appendChild(cardTopNode(result));

    if (profileData) {
      profileData._result = result;
      var details = fragDetailsNode(profileData);
      if (details) card.appendChild(details);
    }

    var ctaWrap = el('div', 'cta-wrap');
    var cta = el('a', 'btn-cta', 'Ver Producto →');
    cta.href = productPath(result.handle);
    ctaWrap.appendChild(cta);
    card.appendChild(ctaWrap);

    return card;
  }

  function showProduct(result, profileData) {
    var card = renderProductCard(result, profileData);
    clear(productPanel).appendChild(card);
    productPanel.classList.add('visible');
    productPanel.scrollTop = 0;
    var dismissBtn = document.getElementById('btnDismiss');
    if (dismissBtn) dismissBtn.addEventListener('click', resumeScanning);
  }

  function showVariants(brand, name, currentHandle, renderToken) {
    var ctrl = new AbortController();
    setTimeout(function() { ctrl.abort(); }, 8000);
    fetch(API + '/api/search?q=' + encodeURIComponent(brand + ' ' + name) + '&limit=10', { signal: ctrl.signal })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) {
        if (renderToken !== activeRenderToken) return;
        if (!data || !data.results || data.results.length < 2) return;
        var others = data.results.filter(function(r) { return r.handle !== currentHandle; });
        if (!others.length) return;

        var card = getActiveProductCard(currentHandle);
        var ctaNode = card && card.querySelector('.cta-wrap');
        if (!card || !ctaNode || card.querySelector('.size-variants')) return;

        var wrap = el('div', 'size-variants');
        others.forEach(function(r) {
          var a = el('a', 'size-pill');
          a.href = productPath(r.handle);
          var sizeMatch = (r.title || '').match(/(\d+\s*ml)/i);
          var label = sizeMatch ? sizeMatch[1] : (r.title || '');
          a.textContent = label + ' · ' + fmtPrice(r.price);
          wrap.appendChild(a);
        });
        card.insertBefore(wrap, ctaNode);
      }).catch(function() {});
  }

  function derivePurchaseHandlesFromOrders(data) {
    if (!data) return [];
    var orders = Array.isArray(data.orders) ? data.orders : [];
    var handles = [];
    orders.forEach(function(order) {
      if (!order || order.cancelled_at || order.financial_status !== 'paid' || order.fulfillment_status !== 'fulfilled') {
        return;
      }
      var items = Array.isArray(order.line_items) ? order.line_items : [];
      items.forEach(function(item) {
        var handle = normalizeHandle(
          item && (
            item.handle ||
            (item.product && item.product.handle) ||
            (item.product_exists && item.url && extractHandleFromPath(item.url))
          )
        );
        if (handle) handles.push(handle);
      });
    });
    return uniqueHandles(handles);
  }
  function extractHandleFromPath(value) {
    if (!value) return '';
    var match = String(value).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  var loadPurchases = createRetryableMemoizedLoader(function() {
    var ctrl = new AbortController();
    var to = setTimeout(function() { ctrl.abort(); }, 8000);
    return fetch('/account/orders.json?limit=20', {
      credentials: 'same-origin',
      signal: ctrl.signal
    })
      .then(function(r) {
        clearTimeout(to);
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function(data) {
        var derived = derivePurchaseHandlesFromOrders(data);
        if (derived.length) {
          PURCHASES.splice.apply(PURCHASES, [0, PURCHASES.length].concat(derived));
          window.PRIVE_USER_PURCHASES = PURCHASES.slice();
        }
        return PURCHASES;
      })
      .catch(function(error) {
        clearTimeout(to);
        throw error;
      });
  });

  function ensurePurchasesLoaded() {
    if (PURCHASES.length || !CUSTOMER_ID) return Promise.resolve(PURCHASES);
    return loadPurchases().catch(function() {
      return PURCHASES;
    });
  }

  function prettyHandle(h) {
    return String(h || '').replace(/-/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }
  function showPurchaseSimilarity(scannedHandle, renderToken) {
    return ensurePurchasesLoaded().then(function() {
      if (!PURCHASES.length) return;
      var body = JSON.stringify({ handle: scannedHandle, against: PURCHASES.slice(0, 30) });
      var ctrl = new AbortController();
      setTimeout(function() { ctrl.abort(); }, 8000);
      return fetch(API + '/api/similarity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        signal: ctrl.signal
      })
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) {
          if (renderToken !== activeRenderToken) return;
          if (!data || !data.scores || !data.scores.length) return;
          var top = data.scores.filter(function(s) {
            return typeof s.score === 'number' && s.score >= 0.5;
          }).slice(0, 4);
          if (!top.length) return;

          var card = getActiveProductCard(scannedHandle);
          var ctaNode = card && card.querySelector('.cta-wrap');
          if (!card || !ctaNode || card.querySelector('.purchase-similarity')) return;

          var wrap = el('div', 'purchase-similarity');
          wrap.appendChild(el('div', 'purchase-similarity-label', 'Similar a lo que compraste'));
          top.forEach(function(s) {
            var pct = Math.round(s.score * 100);
            var chip = el('a', 'purchase-chip');
            chip.href = productPath(s.handle);
            chip.appendChild(el('span', 'score', pct + '%'));
            chip.appendChild(el('span', null, prettyHandle(s.handle)));
            wrap.appendChild(chip);
          });

          card.insertBefore(wrap, ctaNode);
        })
        .catch(function() {});
    });
  }

  function pauseScanning() {
    scanningPaused = true;
  }
  function resumeScanning() {
    if (uploadMode) {
      uploadMode = false;
      clearMatch();
      permScreen.classList.remove('hidden');
      setStatus('Listo', '');
      return;
    }
    scanningPaused = false;
    clearMatch();
    setStatus('Listo', '');
    setScan(false);
    setBrackets(null);
    if (tapOverlay) tapOverlay.classList.remove('disabled');
    if (scanHint) scanHint.classList.remove('hidden');
  }

  function processMatch(data) {
    var result = data.results[0];
    var handle = result.handle;
    var isSame = handle === lastHandle;
    lastHandle = handle;

    showIdentification(data);
    setStatus('Identificado', 'identified');
    setScan(false);
    pauseScanning();

    if (isSame && productPanel.classList.contains('visible')) {
      productPanel.style.transform = 'translateY(2px)';
      setTimeout(function() { productPanel.style.transform = ''; }, 200);
      return;
    }

    var renderToken = ++activeRenderToken;
    // Profile data is now embedded in the result — no second fetch needed
    var profileData = result.profile || null;
    if (profileData) {
      profileData._result = result;
    }
    showProduct(result, profileData);
    var id = data.identification;
    if (id && id.brand && id.name) showVariants(id.brand, id.name, handle, renderToken);
    showPurchaseSimilarity(handle, renderToken);
  }

  function clearMatch() {
    activeRenderToken += 1;
    idOverlay.classList.remove('visible');
    productPanel.classList.remove('visible');
    clear(productPanel);
    lastHandle = null;
  }

  function captureFrame() {
    if (inflight || !stream || !video.videoWidth) return;
    inflight = true;
    shutterSound.currentTime = 0;
    shutterSound.play().catch(function() {});
    cameraFlash();
    setStatus('Analizando...', '');
    setScan(true);
    if (firstScan) {
      firstScan = false;
      if (scanHint) scanHint.classList.add('hidden');
    }
    if (tapOverlay) tapOverlay.classList.add('disabled');

    ctx.drawImage(video, 0, 0, W, H);
    canvas.toBlob(function(blob) {
      if (!blob) { inflight = false; return; }
      postVision(blob,
        function(data) { processMatch(data); inflight = false; },
        function() {
          clearMatch();
          setStatus('Intenta de nuevo', '');
          setScan(false);
          setBrackets('no-match');
          setTimeout(function() { setBrackets(null); }, 600);
          inflight = false;
          if (tapOverlay) tapOverlay.classList.remove('disabled');
          if (scanHint) { scanHint.classList.remove('hidden'); }
        },
        function(err) {
          if (err && err.name === 'AbortError') {
            showToast('Tiempo agotado', 'La solicitud tardó demasiado. Reintentando...');
          } else {
            showToast('Error de conexión', (err && err.message) || 'No se pudo analizar la imagen.');
          }
          setStatus('Listo', '');
          setScan(false);
          inflight = false;
          if (tapOverlay) tapOverlay.classList.remove('disabled');
          if (scanHint) { scanHint.classList.remove('hidden'); }
        }
      );
    }, 'image/jpeg', QUAL);
  }

  function handleUpload(file) {
    uploadMode = true;
    permScreen.classList.add('hidden');
    errorScreen.classList.add('hidden');
    setStatus('Analizando...', '');

    var ocr = window.PriveBrainOcr;
    var attempt = ocr ? tryOcr(file) : Promise.reject(new Error('ocr-unavailable'));

    attempt
      .then(function(textSearchData) {
        if (textSearchData && textSearchData.results && textSearchData.results.length) {
          processMatch(textSearchData);
        } else {
          runVisionFallback(file);
        }
      })
      .catch(function() { runVisionFallback(file); });
  }

  function tryOcr(file) {
    return getOcrWorker().then(function(worker) {
      return worker.recognize(file).then(function(res) {
        var text = (res && res.data && res.data.text) || '';
        var norm = window.PriveBrainOcr.normalizeOcr(text);
        if (!window.PriveBrainOcr.isUsableOcr(norm.tokens)) return null;

        var ctrl = new AbortController();
        setTimeout(function() { ctrl.abort(); }, 8000);
        return fetch(API + '/api/search?q=' + encodeURIComponent(norm.text) + '&limit=8', { signal: ctrl.signal })
          .then(function(r) { return r.ok ? r.json() : null; });
      });
    });
  }

  function runVisionFallback(file) {
    postVision(file,
      function(data) { processMatch(data); },
      function() {
        showToast('No Encontrado', 'No pudimos identificar ese perfume. Intenta con otra foto.');
        uploadMode = false;
        permScreen.classList.remove('hidden');
        setStatus('Listo', '');
      },
      function(err) {
        if (err && err.name === 'AbortError') {
          showToast('Tiempo Agotado', 'La solicitud tardó demasiado.');
        } else {
          showToast('Error', (err && err.message) || 'No se pudo analizar la imagen.');
        }
        uploadMode = false;
        permScreen.classList.remove('hidden');
        setStatus('Listo', '');
      }
    );
  }

  function startCamera() {
    if (stream || cameraStarting) return;
    cameraStarting = true;
    permScreen.classList.add('hidden');
    errorScreen.classList.add('hidden');
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: false
    }).then(function(s) {
      cameraStarting = false;
      stream = s;
      video.srcObject = s;
      video.play();
      setStatus('Listo', '');
      setScan(false);
      initParticles();
      setBrackets('pulse');
      setTimeout(function() { setBrackets(null); }, 800);
    }).catch(function(err) {
      cameraStarting = false;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        showError('Permiso Denegado', 'Habilita el acceso a la cámara en la configuración de tu navegador.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        showError('Sin Cámara', 'No se encontró una cámara en este dispositivo.');
      } else {
        showError('Error', 'No se pudo iniciar la cámara: ' + err.message);
      }
    });
  }

  function stopCamera() {
    cameraStarting = false;
    if (animFrame) cancelAnimationFrame(animFrame);
    if (stream) {
      stream.getTracks().forEach(function(t) { t.stop(); });
      stream = null;
    }
    inflight = false;
  }

  window.addEventListener('resize', function() {
    if (!stream) return;
    var rect = particlesCanvas.getBoundingClientRect();
    particlesCanvas.width = rect.width;
    particlesCanvas.height = rect.height;
  });

  document.getElementById('btnGrant').addEventListener('click', startCamera);
  document.getElementById('btnRetry').addEventListener('click', startCamera);
  document.getElementById('btnClose').addEventListener('click', function() {
    stopCamera();
    if (window.history.length > 1) window.history.back();
    else window.location.href = '/';
  });

  // Tap overlay: tap viewport to scan
  if (tapOverlay) {
    tapOverlay.addEventListener('click', function() {
      if (!inflight && stream && !scanningPaused) captureFrame();
    });
  }

  // Upload icon in top bar
  var btnUploadTop = document.getElementById('btnUploadTop');
  if (btnUploadTop) {
    btnUploadTop.addEventListener('click', function() {
      document.getElementById('fileInput').click();
    });
  }

  document.getElementById('btnUpload').addEventListener('click', function() {
    document.getElementById('fileInput').click();
  });
  document.getElementById('fileInput').addEventListener('change', function(e) {
    var file = e.target.files[0];
    if (!file) return;
    handleUpload(file);
    e.target.value = '';
  });

  if (navigator.permissions && navigator.permissions.query) {
    navigator.permissions.query({ name: 'camera' })
      .then(function(r) { if (r.state === 'granted') startCamera(); })
      .catch(function() {});
  }
})();